import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import MainLayout from "@/components/MainLayout";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Clock,
  CheckCircle2,
  XCircle,
  Ban,
  RotateCcw,
  Send,
  X,
  RefreshCw,
  Eye,
  EyeOff,
  MousePointerClick,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";

const STATUS_CONFIG = {
  pending: { icon: Clock, color: "bg-amber-500/15 text-amber-400", label: "Pending" },
  sent: { icon: CheckCircle2, color: "bg-emerald-500/15 text-emerald-400", label: "Sent" },
  failed: { icon: XCircle, color: "bg-red-500/15 text-red-400", label: "Failed" },
  cancelled: { icon: Ban, color: "bg-gray-500/15 text-gray-400", label: "Cancelled" },
  bounced: { icon: XCircle, color: "bg-orange-500/15 text-orange-400", label: "Bounced" },
  complained: { icon: Ban, color: "bg-red-500/15 text-red-400", label: "Complained" },
};

export default function EmailQueue() {
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState({ pending: 0, sent: 0, failed: 0, cancelled: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  // Banner/brand filters
  const [filterBanner, setFilterBanner] = useState("");
  const [filterBrand, setFilterBrand] = useState("");

  // Sorting
  const [sortCol, setSortCol] = useState("");
  const [sortDir, setSortDir] = useState("asc");

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (filter) params.status = filter;
      if (filterBanner) params.banner_id = filterBanner;

      const [jobsRes, statsRes] = await Promise.all([
        axios.get(`${API}/email-queue`, { params }),
        axios.get(`${API}/email-queue/stats`),
      ]);
      setJobs(jobsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to load email queue");
    } finally {
      setLoading(false);
    }
  }, [filter, filterBanner]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const cancelJob = async (jobId) => {
    try {
      await axios.post(`${API}/email-queue/cancel/${jobId}`);
      toast.success("Email cancelled");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel");
    }
  };

  const retryJob = async (jobId) => {
    try {
      await axios.post(`${API}/email-queue/retry/${jobId}`);
      toast.success("Email queued for retry");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to retry");
    }
  };

  const cancelLottery = async (lotteryId, lotteryName) => {
    if (!confirm(`Cancel all pending emails for ${lotteryName}?`)) return;
    try {
      const res = await axios.post(`${API}/email-queue/cancel-lottery/${lotteryId}`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error("Failed to cancel emails");
    }
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const formatTime = (iso) => {
    if (!iso) return "-";
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  // Sort handler
  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  };

  const getSortIcon = (col) => {
    if (sortCol !== col) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    return sortDir === "asc" ? <ArrowUp className="w-3 h-3 ml-1" /> : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  // Apply sorting
  const sortedJobs = [...jobs].sort((a, b) => {
    if (!sortCol) return 0;
    let valA, valB;
    if (sortCol === "send_at" || sortCol === "sent_at") {
      valA = a[sortCol] || "";
      valB = b[sortCol] || "";
    } else if (sortCol === "open_count" || sortCol === "click_count") {
      valA = a[sortCol] || 0;
      valB = b[sortCol] || 0;
      return sortDir === "asc" ? valA - valB : valB - valA;
    } else {
      valA = (a[sortCol] || "").toString().toLowerCase();
      valB = (b[sortCol] || "").toString().toLowerCase();
    }
    if (valA < valB) return sortDir === "asc" ? -1 : 1;
    if (valA > valB) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  // Apply brand filter client-side (jobs may not have brand_id directly)
  const filteredJobs = filterBrand
    ? sortedJobs.filter(j => j.brand_id === filterBrand)
    : sortedJobs;

  // Unique banners/brands for filter dropdowns
  const uniqueBanners = [...new Map(jobs.map(j => [j.banner_id, { id: j.banner_id, name: j.banner_name }])).values()];
  const uniqueBrands = [...new Map(jobs.filter(j => j.brand_id).map(j => [j.brand_id, { id: j.brand_id, name: j.brand_name || "Unknown" }])).values()];

  // Group pending jobs by lottery for cancel-all
  const pendingLotteries = {};
  jobs.filter(j => j.status === "pending").forEach(j => {
    if (!pendingLotteries[j.lottery_id]) {
      pendingLotteries[j.lottery_id] = { name: j.lottery_name, count: 0 };
    }
    pendingLotteries[j.lottery_id].count++;
  });

  const filterButtons = [
    { value: "", label: "All", count: stats.total },
    { value: "pending", label: "Pending", count: stats.pending },
    { value: "sent", label: "Sent", count: stats.sent },
    { value: "failed", label: "Failed", count: stats.failed },
    { value: "cancelled", label: "Cancelled", count: stats.cancelled },
  ];

  return (
    <MainLayout title="Email Queue" subtitle="Monitor and manage automated banner email delivery">
      <div className="space-y-6">
        {/* Status filter row */}
        <div className="flex items-center gap-3 flex-wrap">
          {filterButtons.map((fb) => (
            <button
              key={fb.value}
              onClick={() => setFilter(fb.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                filter === fb.value
                  ? "bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/30"
                  : "bg-white/5 text-gray-400 border border-white/10 hover:border-white/20"
              }`}
              data-testid={`filter-${fb.value || "all"}`}
            >
              {fb.label}
              <span className="ml-2 text-xs opacity-70">{fb.count}</span>
            </button>
          ))}
          <Button variant="ghost" size="sm" onClick={fetchData} className="text-gray-400 hover:text-white ml-auto" data-testid="refresh-queue-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {/* Banner/Brand filters */}
        <div className="flex items-center gap-3 flex-wrap mt-6">
          <select value={filterBanner} onChange={(e) => setFilterBanner(e.target.value)} className="h-9 px-3 rounded-lg bg-[#1a1a24] border border-white/10 text-sm text-gray-300 focus:border-[#10B981] outline-none" data-testid="filter-queue-banner">
            <option value="">All Banners</option>
            {uniqueBanners.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <select value={filterBrand} onChange={(e) => setFilterBrand(e.target.value)} className="h-9 px-3 rounded-lg bg-[#1a1a24] border border-white/10 text-sm text-gray-300 focus:border-[#10B981] outline-none" data-testid="filter-queue-brand">
            <option value="">All Brands</option>
            {uniqueBrands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          {(filterBanner || filterBrand) && (
            <Button variant="ghost" size="sm" onClick={() => { setFilterBanner(""); setFilterBrand(""); }} className="text-gray-400 hover:text-white text-xs" data-testid="clear-queue-filters">
              Clear filters
            </Button>
          )}
        </div>

        {/* Pending lottery cancel buttons */}
        {Object.keys(pendingLotteries).length > 0 && (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-medium text-amber-300">Pending Emails</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(pendingLotteries).map(([lotteryId, info]) => (
                <Button
                  key={lotteryId}
                  variant="outline"
                  size="sm"
                  onClick={() => cancelLottery(lotteryId, info.name)}
                  className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 text-xs"
                  data-testid={`cancel-lottery-${lotteryId}`}
                >
                  <X className="w-3 h-3 mr-1" />
                  Cancel {info.name} ({info.count})
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Jobs table */}
        {loading ? (
          <div className="flex items-center justify-center py-12 mt-6">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#10B981]"></div>
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="bg-white/5 border border-white/10 rounded-xl p-12 text-center mt-6">
            <Send className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No emails {filter ? `with status "${filter}"` : "in queue"}</h3>
            <p className="text-gray-400">Emails are automatically queued when lottery results are updated.</p>
          </div>
        ) : (
          <div className="bg-white/5 border border-white/10 rounded-xl overflow-x-auto mt-6">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-gray-400 cursor-pointer select-none" onClick={() => handleSort("status")}>
                    <span className="inline-flex items-center">Status{getSortIcon("status")}</span>
                  </TableHead>
                  <TableHead className="text-gray-400 cursor-pointer select-none" onClick={() => handleSort("recipient_name")}>
                    <span className="inline-flex items-center">Recipient{getSortIcon("recipient_name")}</span>
                  </TableHead>
                  <TableHead className="text-gray-400 cursor-pointer select-none" onClick={() => handleSort("banner_name")}>
                    <span className="inline-flex items-center">Banner{getSortIcon("banner_name")}</span>
                  </TableHead>
                  <TableHead className="text-gray-400 cursor-pointer select-none" onClick={() => handleSort("lottery_name")}>
                    <span className="inline-flex items-center">Lottery{getSortIcon("lottery_name")}</span>
                  </TableHead>
                  <TableHead className="text-gray-400 cursor-pointer select-none" onClick={() => handleSort("send_at")}>
                    <span className="inline-flex items-center">Scheduled{getSortIcon("send_at")}</span>
                  </TableHead>
                  <TableHead className="text-gray-400 cursor-pointer select-none" onClick={() => handleSort("sent_at")}>
                    <span className="inline-flex items-center">Sent{getSortIcon("sent_at")}</span>
                  </TableHead>
                  <TableHead className="text-gray-400 text-center">Tracking</TableHead>
                  <TableHead className="text-gray-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredJobs.map((job) => (
                  <TableRow key={job.id} className="border-white/10 hover:bg-white/5" data-testid={`queue-row-${job.id}`}>
                    <TableCell>{getStatusBadge(job.status)}</TableCell>
                    <TableCell>
                      <div>
                        <span className="text-white text-sm">{job.recipient_name || "-"}</span>
                        <span className="text-gray-500 text-xs block">{job.recipient_email}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-300 text-sm">{job.banner_name}</TableCell>
                    <TableCell className="text-gray-300 text-sm">{job.lottery_name}</TableCell>
                    <TableCell className="text-gray-400 text-sm">{formatTime(job.send_at)}</TableCell>
                    <TableCell className="text-gray-400 text-sm">
                      {job.sent_at ? formatTime(job.sent_at) : "-"}
                      {job.error && (
                        <span className="text-red-400 text-xs block" title={job.error}>
                          {job.error.substring(0, 40)}{job.error.length > 40 ? "..." : ""}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {job.status === "sent" ? (
                        <div className="flex items-center justify-center gap-2">
                          {job.open_count > 0 ? (
                            <span className="inline-flex items-center gap-1 text-xs text-emerald-400" title={`Opened ${job.open_count} time${job.open_count !== 1 ? 's' : ''}`} data-testid={`opened-${job.id}`}>
                              <Eye className="w-3 h-3" />{job.open_count}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-600" title="Not opened"><EyeOff className="w-3 h-3" /></span>
                          )}
                          {job.click_count > 0 ? (
                            <span className="inline-flex items-center gap-1 text-xs text-blue-400" title={`Clicked ${job.click_count} time${job.click_count !== 1 ? 's' : ''}`} data-testid={`clicked-${job.id}`}>
                              <MousePointerClick className="w-3 h-3" />{job.click_count}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-600" title="No clicks"><MousePointerClick className="w-3 h-3" /></span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-600">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {job.status === "pending" && (
                        <Button variant="ghost" size="sm" onClick={() => cancelJob(job.id)} className="text-gray-400 hover:text-red-400 hover:bg-red-500/10 text-xs" data-testid={`cancel-job-${job.id}`}>
                          <X className="w-3 h-3 mr-1" />Cancel
                        </Button>
                      )}
                      {job.status === "failed" && (
                        <Button variant="ghost" size="sm" onClick={() => retryJob(job.id)} className="text-gray-400 hover:text-amber-400 hover:bg-amber-500/10 text-xs" data-testid={`retry-job-${job.id}`}>
                          <RotateCcw className="w-3 h-3 mr-1" />Retry
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
